import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { Briefcase, CalendarRange, ChevronDown, ChevronRight, Clock, Download, Loader2, Pencil, Plus, Receipt, RotateCcw, Trash2, Users, ListChecks, ChartNoAxesCombined, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha, hoyISO } from "@/lib/format";
import { useEmpresa } from "@/hooks/useEmpresa";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { HorarioEditor, HORARIO_DEFECTO, horarioParaGuardar, normalizarHorario, resumenHorario } from "@/components/HorarioEditor";
import ProyectosCalendario from "@/pages/admin/ProyectosCalendario";
import ProyectosClientes from "@/pages/admin/ProyectosClientes";
import ProyectosRentabilidad from "@/pages/admin/ProyectosRentabilidad";
import ProyectosTimeline from "@/pages/admin/ProyectosTimeline";
import ProyectoTareasDialog from "@/pages/admin/ProyectoTareasDialog";

const FORM_CREAR_VACIO = {
  nombre: "", codigo: "", descripcion: "", color: "#6366f1", fecha_inicio: hoyISO(), fecha_fin: "",
  cliente_id: "", presupuesto_horas: "", presupuesto_euros: "", tarifa_venta_hora: "", tarifa_coste_hora: "", modo_tarifas: "desactivado",
};

const ESTADO_EJECUCION = {
  planificado: { label: "Planificado", cls: "border-sky-200 bg-sky-50 text-sky-800" },
  en_curso: { label: "En curso", cls: "border-emerald-200 bg-emerald-50 text-emerald-800" },
  finalizado: { label: "Finalizado", cls: "border-zinc-200 bg-zinc-100 text-zinc-600" },
  sin_fechas: { label: "Sin fechas", cls: "border-amber-200 bg-amber-50 text-amber-800" },
  inactivo: { label: "Inactivo", cls: "border-rose-200 bg-rose-50 text-rose-700" },
};

const ESTADO_PERIODO_ASIG = {
  activa: { label: "Activo hoy", cls: "bg-emerald-100 text-emerald-800" },
  futura: { label: "Próximo", cls: "bg-sky-100 text-sky-800" },
  pasada: { label: "Periodo pasado", cls: "bg-zinc-100 text-zinc-600" },
  finalizada: { label: "Baja", cls: "bg-zinc-100 text-zinc-500" },
};

function inicioAsignacionDefecto(proyecto) {
  const hoy = hoyISO();
  const ini = proyecto.fecha_inicio || hoy;
  return ini > hoy ? ini : hoy;
}

function validarFechasAsignacion(proyecto, fechaInicio, fechaFin) {
  if (!fechaInicio) return "Indica la fecha de inicio de la asignación";
  if (fechaFin && fechaFin < fechaInicio) return "La fecha de fin debe ser posterior al inicio";
  if (proyecto.fecha_inicio && fechaInicio < proyecto.fecha_inicio) {
    return `La asignación no puede empezar antes del inicio del proyecto (${fmtFecha(proyecto.fecha_inicio)})`;
  }
  if (proyecto.fecha_fin) {
    if (fechaInicio > proyecto.fecha_fin) return "La fecha de inicio es posterior al fin del proyecto";
    const finEfectiva = fechaFin || proyecto.fecha_fin;
    if (finEfectiva > proyecto.fecha_fin) {
      return `La asignación no puede terminar después del fin del proyecto (${fmtFecha(proyecto.fecha_fin)})`;
    }
  }
  return null;
}

function BarraCarga({ pct, color }) {
  const v = Math.min(100, Math.max(0, pct));
  const alerta = v > 100;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[10px] font-medium uppercase tracking-wide text-zinc-500">
        <span>Carga equipo</span>
        <span className={alerta ? "text-rose-600" : ""}>{v}%</span>
      </div>
      <div className="h-2.5 overflow-hidden rounded-full bg-zinc-100">
        <div
          className={`h-full rounded-full transition-all ${alerta ? "bg-rose-500" : ""}`}
          style={{ width: `${Math.min(v, 100)}%`, backgroundColor: alerta ? undefined : color }}
        />
      </div>
    </div>
  );
}

function TarjetaProyecto({ proyecto, onAsignar, onAsignarHorario, onVerEquipo, onEditar, onEliminar, onPicados, onGastos, onReactivar, onTareas, onRentabilidad }) {
  const n = proyecto.num_trabajadores ?? 0;
  const nProg = proyecto.num_trabajadores_programados ?? n;
  const inactivo = proyecto.activo === false;
  const ejec = inactivo
    ? ESTADO_EJECUCION.inactivo
    : (ESTADO_EJECUCION[proyecto.estado_ejecucion] || ESTADO_EJECUCION.sin_fechas);
  if (inactivo) {
    return (
      <article className="overflow-hidden rounded-2xl border border-dashed border-rose-200 bg-rose-50/30 shadow-sm">
        <div className="flex items-center gap-3 p-4">
          <div className="w-1.5 shrink-0 self-stretch rounded-full bg-rose-300" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-400">#{proyecto.codigo || "—"}</p>
              <Badge variant="outline" className={`text-[10px] ${ESTADO_EJECUCION.inactivo.cls}`}>Inactivo</Badge>
            </div>
            <h3 className="text-lg font-semibold text-zinc-700">{proyecto.nombre}</h3>
            {proyecto.descripcion && (
              <p className="mt-0.5 text-sm text-zinc-500 line-clamp-1">{proyecto.descripcion}</p>
            )}
          </div>
          <div className="flex shrink-0 gap-2">
            <Button type="button" size="sm" className="h-8 gap-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => onReactivar(proyecto)}>
              <RotateCcw className="h-3.5 w-3.5" /> Reactivar
            </Button>
            <Button type="button" variant="outline" size="sm" className="h-8 gap-1 text-rose-600 hover:text-rose-700" onClick={() => onEliminar(proyecto)}>
              <Trash2 className="h-3.5 w-3.5" /> Eliminar
            </Button>
          </div>
        </div>
      </article>
    );
  }
  return (
    <article className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
      <div className="flex min-h-[140px]">
        <div className="w-1.5 shrink-0" style={{ backgroundColor: proyecto.color }} />
        <div className="flex flex-1 flex-col gap-4 p-4 lg:flex-row lg:items-stretch">
          <div className="min-w-[200px] shrink-0 lg:w-56">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-400">#{proyecto.codigo || "—"}</p>
            <h3 className="text-lg font-semibold text-zinc-900">{proyecto.nombre}</h3>
            {proyecto.descripcion && (
              <p className="mt-1 text-sm text-zinc-500 line-clamp-2">{proyecto.descripcion}</p>
            )}
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge variant="outline" className={`text-[10px] ${ejec.cls}`}>{ejec.label}</Badge>
              {proyecto.cliente_nombre && <Badge variant="outline" className="text-[10px] text-violet-700">{proyecto.cliente_nombre}</Badge>}
              <Badge variant="outline" className="text-[10px]">{proyecto.num_tareas || 0} tareas</Badge>
              <p className="text-sm text-zinc-600">
                <span className="font-medium">Ejecución:</span>{" "}
                {proyecto.fecha_inicio ? fmtFecha(proyecto.fecha_inicio) : "Sin fecha"}
                {proyecto.fecha_fin ? ` → ${fmtFecha(proyecto.fecha_fin)}` : proyecto.fecha_inicio ? " (sin fin)" : ""}
              </p>
            </div>
            {proyecto.tiene_horario ? (
              <p className="mt-1 text-xs text-violet-700">{resumenHorario(proyecto.horario)}</p>
            ) : (
              <Badge variant="outline" className="mt-2 text-[10px] text-amber-700 border-amber-200">Sin horario</Badge>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              <Button type="button" variant="outline" size="sm" className="h-8 gap-1" onClick={() => onEditar(proyecto)}>
                <Pencil className="h-3.5 w-3.5" /> Editar
              </Button>
              <Button type="button" variant="outline" size="sm" className="h-8 gap-1 text-rose-600 hover:text-rose-700" onClick={() => onEliminar(proyecto)}>
                <Trash2 className="h-3.5 w-3.5" /> Eliminar
              </Button>
            </div>
          </div>

          <div className="flex min-w-0 flex-1 flex-col justify-center border-t border-zinc-100 pt-4 lg:border-l lg:border-t-0 lg:pl-6 lg:pt-0">
            <BarraCarga pct={proyecto.carga_total_pct || 0} color={proyecto.color} />
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2 text-sm text-zinc-600">
                <Users className="h-4 w-4 text-zinc-400" />
                <span>
                  {nProg > n
                    ? `${n} activo${n === 1 ? "" : "s"} hoy · ${nProg} programado${nProg === 1 ? "" : "s"}`
                    : `${n} trabajador${n === 1 ? "" : "es"} asignado${n === 1 ? "" : "s"}`}
                </span>
              </div>
              <Button type="button" variant="outline" size="sm" className="h-8 gap-1.5" onClick={() => onVerEquipo(proyecto)}>
                <Users className="h-3.5 w-3.5" />
                Ver y gestionar
              </Button>
            </div>
          </div>

          <div className="flex shrink-0 flex-col justify-center gap-2 lg:w-48">
            <Button type="button" variant="outline" className="w-full gap-1.5" onClick={() => onPicados(proyecto)}>
              <Clock className="h-4 w-4" /> Picados
            </Button>
            <Button type="button" variant="outline" className="w-full gap-1.5" onClick={() => onGastos(proyecto)}>
              <Receipt className="h-4 w-4" /> Gastos
            </Button>
            <Button type="button" variant="outline" className="w-full gap-1.5" onClick={() => onTareas(proyecto)}>
              <ListChecks className="h-4 w-4" /> Tareas
            </Button>
            <Button type="button" variant="outline" className="w-full gap-1.5" onClick={() => onRentabilidad(proyecto)}>
              <ChartNoAxesCombined className="h-4 w-4" /> Rentabilidad
            </Button>
            <Button type="button" variant="outline" className="w-full gap-1.5" onClick={() => onAsignarHorario(proyecto)}>
              <Clock className="h-4 w-4" /> Asignar horario
            </Button>
            <Button type="button" className="w-full gap-1.5" onClick={() => onAsignar(proyecto)}>
              <Plus className="h-4 w-4" /> Asignar trabajador
            </Button>
          </div>
        </div>
      </div>
    </article>
  );
}

function CamposEconomicos({ form, setForm, clientes }) {
  return <div className="space-y-3 border-t pt-3">
    <div><Label>Cliente</Label><Select value={String(form.cliente_id || "sin-cliente")} onValueChange={(v) => setForm({ ...form, cliente_id: v === "sin-cliente" ? "" : v })}><SelectTrigger><SelectValue placeholder="Sin cliente" /></SelectTrigger><SelectContent><SelectItem value="sin-cliente">Sin cliente</SelectItem>{clientes.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.nombre}</SelectItem>)}</SelectContent></Select></div>
    <div className="grid grid-cols-2 gap-3"><div><Label>Presupuesto horas</Label><Input type="number" value={form.presupuesto_horas ?? ""} onChange={(e) => setForm({ ...form, presupuesto_horas: e.target.value })} /></div><div><Label>Presupuesto €</Label><Input type="number" value={form.presupuesto_euros ?? ""} onChange={(e) => setForm({ ...form, presupuesto_euros: e.target.value })} /></div></div>
    <div><Label>Modo de tarifas</Label><Select value={form.modo_tarifas || "desactivado"} onValueChange={(v) => setForm({ ...form, modo_tarifas: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="desactivado">Desactivado</SelectItem><SelectItem value="proyecto">Proyecto</SelectItem><SelectItem value="tarea">Tarea</SelectItem><SelectItem value="persona">Persona</SelectItem></SelectContent></Select></div>
    {form.modo_tarifas === "proyecto" && <div className="grid grid-cols-2 gap-3"><div><Label>Tarifa venta €/h</Label><Input type="number" value={form.tarifa_venta_hora ?? ""} onChange={(e) => setForm({ ...form, tarifa_venta_hora: e.target.value })} /></div><div><Label>Tarifa coste €/h</Label><Input type="number" value={form.tarifa_coste_hora ?? ""} onChange={(e) => setForm({ ...form, tarifa_coste_hora: e.target.value })} /></div></div>}
  </div>;
}

function FormProyectoCrear({ form, setForm, clientes }) {
  return (
    <div className="space-y-3">
      <div><Label>Código proyecto *</Label><Input value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase() })} placeholder="Ej. PRY-2026-01" /></div>
      <div><Label>Nombre *</Label><Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></div>
      <div><Label>Descripción</Label><Input value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Inicio *</Label><Input type="date" value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} /></div>
        <div><Label>Fin</Label><Input type="date" value={form.fecha_fin} onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })} /></div>
      </div>
      <div><Label>Color</Label><Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-10 w-20" /></div>
      <CamposEconomicos form={form} setForm={setForm} clientes={clientes} />
      <p className="text-xs text-zinc-500">Podrás definir el horario después con «Asignar horario».</p>
    </div>
  );
}

function FormProyectoEditar({ form, setForm, clientes }) {
  return (
    <div className="space-y-3">
      <div><Label>Código proyecto *</Label><Input value={form.codigo || ""} onChange={(e) => setForm({ ...form, codigo: e.target.value.toUpperCase() })} /></div>
      <div><Label>Nombre *</Label><Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></div>
      <div><Label>Descripción</Label><Input value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Inicio *</Label><Input type="date" value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} /></div>
        <div><Label>Fin</Label><Input type="date" value={form.fecha_fin} onChange={(e) => setForm({ ...form, fecha_fin: e.target.value })} /></div>
      </div>
      <div className="flex items-center gap-2">
        <Switch checked={form.activo} onCheckedChange={(v) => setForm({ ...form, activo: v })} />
        <Label>Proyecto activo</Label>
      </div>
      <div><Label>Color</Label><Input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-10 w-20" /></div>
      <CamposEconomicos form={form} setForm={setForm} clientes={clientes} />
    </div>
  );
}

export default function Proyectos() {
  const { empresa, cargando: cargandoEmpresa } = useEmpresa();
  const [vista, setVista] = useState("tarjetas");
  const [verInactivos, setVerInactivos] = useState(false);
  const [items, setItems] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [tareasProyecto, setTareasProyecto] = useState(null);
  const [rentabilidadProyecto, setRentabilidadProyecto] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [proyOpen, setProyOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formProy, setFormProy] = useState(FORM_CREAR_VACIO);
  const [formEdit, setFormEdit] = useState(null);
  const [horarioOpen, setHorarioOpen] = useState(false);
  const [proyectoHorario, setProyectoHorario] = useState(null);
  const [horarioForm, setHorarioForm] = useState(HORARIO_DEFECTO);
  const [asigOpen, setAsigOpen] = useState(false);
  const [equipoOpen, setEquipoOpen] = useState(false);
  const [proyectoEquipoId, setProyectoEquipoId] = useState(null);
  const [proyectoSel, setProyectoSel] = useState(null);
  const [horarioPropio, setHorarioPropio] = useState(false);
  const [formAsig, setFormAsig] = useState({ usuario_id: "", fecha_inicio: hoyISO(), fecha_fin: "", carga_pct: "100", horario: HORARIO_DEFECTO });
  const [picadosOpen, setPicadosOpen] = useState(false);
  const [gastosOpen, setGastosOpen] = useState(false);
  const [proyectoInforme, setProyectoInforme] = useState(null);
  const [picadosData, setPicadosData] = useState(null);
  const [gastosData, setGastosData] = useState(null);
  const [picadosDesde, setPicadosDesde] = useState(() => hoyISO().slice(0, 8) + "01");
  const [picadosHasta, setPicadosHasta] = useState(hoyISO());
  const [picadosAgrupar, setPicadosAgrupar] = useState("dia");
  const [cargandoInforme, setCargandoInforme] = useState(false);

  const ordenarProyectos = (lista) => (
    [...lista].sort((a, b) => {
      const fa = a.fecha_inicio || "9999-12-31";
      const fb = b.fecha_inicio || "9999-12-31";
      return fa.localeCompare(fb) || (a.codigo || "").localeCompare(b.codigo || "", undefined, { numeric: true });
    })
  );

  const proyectosOrdenados = useMemo(
    () => ordenarProyectos(items.filter((p) => p.activo !== false)),
    [items],
  );

  const proyectosInactivos = useMemo(
    () => ordenarProyectos(items.filter((p) => p.activo === false)),
    [items],
  );

  const proyectoEquipo = useMemo(
    () => (proyectoEquipoId ? items.find((p) => p.id === proyectoEquipoId) : null),
    [items, proyectoEquipoId],
  );

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get("/proyectos", { params: { activos: false } });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCargando(false);
    }
  }, []);

  const cargarClientes = useCallback(async () => {
    try { const { data } = await api.get("/proyectos/clientes"); setClientes(data.items || []); } catch { /* se muestra vacío si no hay clientes */ }
  }, []);

  useEffect(() => {
    cargar();
    cargarClientes();
    api.get("/usuarios").then((r) => {
      setEmpleados((r.data || []).filter((u) => u.rol === "worker" && u.activo !== false));
    }).catch(() => {});
  }, [cargar, cargarClientes]);

  const abrirNuevo = () => {
    setEditId(null);
    setFormProy({ ...FORM_CREAR_VACIO, fecha_inicio: hoyISO(), fecha_fin: "" });
    setProyOpen(true);
  };

  const abrirEditar = (p) => {
    setEditId(p.id);
    setFormEdit({
      nombre: p.nombre,
      codigo: p.codigo || "",
      descripcion: p.descripcion || "",
      color: p.color || "#6366f1",
      fecha_inicio: p.fecha_inicio || "",
      fecha_fin: p.fecha_fin || "",
      activo: p.activo !== false,
      cliente_id: p.cliente_id ? String(p.cliente_id) : "",
      presupuesto_horas: p.presupuesto_horas ?? "",
      presupuesto_euros: p.presupuesto_euros ?? "",
      tarifa_venta_hora: p.tarifa_venta_hora ?? "",
      tarifa_coste_hora: p.tarifa_coste_hora ?? "",
      modo_tarifas: p.modo_tarifas || "desactivado",
    });
    setProyOpen(true);
  };

  const numONull = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const payloadProyecto = (form) => ({
    nombre: form.nombre,
    codigo: (form.codigo || "").trim(),
    descripcion: form.descripcion || "",
    color: form.color || "#6366f1",
    fecha_inicio: form.fecha_inicio || null,
    fecha_fin: form.fecha_fin || null,
    activo: form.activo !== false,
    cliente_id: form.cliente_id ? Number(form.cliente_id) : null,
    presupuesto_horas: numONull(form.presupuesto_horas),
    presupuesto_euros: numONull(form.presupuesto_euros),
    tarifa_venta_hora: numONull(form.tarifa_venta_hora),
    tarifa_coste_hora: numONull(form.tarifa_coste_hora),
    modo_tarifas: form.modo_tarifas || "desactivado",
  });

  const guardarProyecto = async () => {
    try {
      if (editId) {
        if (!formEdit?.nombre?.trim()) {
          toast.error("Indica el nombre del proyecto");
          return;
        }
        if (!formEdit?.codigo?.trim()) {
          toast.error("Indica el código del proyecto");
          return;
        }
        if (!formEdit?.fecha_inicio) {
          toast.error("Indica la fecha de inicio del proyecto");
          return;
        }
        if (formEdit.fecha_fin && formEdit.fecha_inicio && formEdit.fecha_fin < formEdit.fecha_inicio) {
          toast.error("La fecha de fin debe ser posterior al inicio");
          return;
        }
        await api.put(`/proyectos/${editId}`, payloadProyecto(formEdit));
        toast.success("Proyecto actualizado");
      } else {
        if (!formProy.nombre.trim()) {
          toast.error("Indica el nombre del proyecto");
          return;
        }
        if (!formProy.fecha_inicio) {
          toast.error("Indica la fecha de inicio");
          return;
        }
        if (!formProy.codigo?.trim()) {
          toast.error("Indica el código del proyecto");
          return;
        }
        if (formProy.fecha_fin && formProy.fecha_fin < formProy.fecha_inicio) {
          toast.error("La fecha de fin debe ser posterior al inicio");
          return;
        }
        await api.post("/proyectos", payloadProyecto(formProy));
        toast.success("Proyecto creado");
      }
      setProyOpen(false);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reactivarProyecto = async (p) => {
    try {
      await api.put(`/proyectos/${p.id}/estado`, { activo: true });
      toast.success("Proyecto reactivado");
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const eliminarProyecto = async (p) => {
    if (!window.confirm(`¿Eliminar el proyecto «${p.nombre}» (#${p.codigo})? Se borrarán también sus asignaciones.`)) return;
    try {
      await api.delete(`/proyectos/${p.id}`);
      toast.success("Proyecto eliminado");
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const abrirAsignarHorario = (proj) => {
    setProyectoHorario(proj);
    setHorarioForm(normalizarHorario(proj.horario || HORARIO_DEFECTO));
    setHorarioOpen(true);
  };

  const guardarHorario = async () => {
    if (!proyectoHorario) return;
    try {
      await api.put(`/proyectos/${proyectoHorario.id}/horario`, {
        horario: horarioParaGuardar(horarioForm),
      });
      toast.success("Horario del proyecto guardado");
      setHorarioOpen(false);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const abrirEquipo = (proj) => {
    setProyectoEquipoId(proj.id);
    setEquipoOpen(true);
  };

  const abrirAsignar = (proj) => {
    setProyectoSel(proj);
    setHorarioPropio(false);
    const ini = inicioAsignacionDefecto(proj);
    let fin = proj.fecha_fin || "";
    if (fin && fin < ini) fin = "";
    setFormAsig({
      usuario_id: "",
      fecha_inicio: ini,
      fecha_fin: fin,
      carga_pct: "100",
      horario: normalizarHorario(proj.horario || HORARIO_DEFECTO),
    });
    setAsigOpen(true);
  };

  const enviarAsignacion = async (opts = {}) => {
    if (!proyectoSel || !formAsig.usuario_id) {
      toast.error("Selecciona trabajador y fechas");
      return;
    }
    const errFechas = validarFechasAsignacion(proyectoSel, formAsig.fecha_inicio, formAsig.fecha_fin || null);
    if (errFechas) {
      toast.error(errFechas);
      return;
    }
    const body = {
      usuario_id: Number(formAsig.usuario_id),
      fecha_inicio: formAsig.fecha_inicio,
      fecha_fin: formAsig.fecha_fin || null,
      carga_pct: Number(formAsig.carga_pct) || 100,
      horario_asignacion: horarioPropio ? horarioParaGuardar(formAsig.horario) : null,
      ...opts,
    };
    try {
      const { data } = await api.post(`/proyectos/${proyectoSel.id}/asignaciones`, body);
      const msg = data.requiere_confirmacion_horario
        ? "Asignado. El trabajador debe confirmar el horario."
        : "Trabajador asignado al proyecto";
      toast.success(msg);
      if (data.avisos_solape?.length) {
        const otros = data.avisos_solape.map((c) => c.proyecto_nombre).join(", ");
        toast.info(`Aviso: en el mismo periodo también está asignado a ${otros}.`, { duration: 6000 });
      }
      setAsigOpen(false);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const abrirPicados = (p) => {
    setProyectoInforme(p);
    const hoy = hoyISO();
    setPicadosDesde(`${hoy.slice(0, 8)}01`);
    setPicadosHasta(hoy);
    setPicadosAgrupar("dia");
    setPicadosData(null);
    setPicadosOpen(true);
  };

  const cargarPicados = async () => {
    if (!proyectoInforme) return;
    setCargandoInforme(true);
    try {
      const { data } = await api.get(`/proyectos/${proyectoInforme.id}/fichajes`, {
        params: { desde: picadosDesde, hasta: picadosHasta, agrupar: picadosAgrupar },
      });
      setPicadosData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCargandoInforme(false);
    }
  };

  const exportarPicados = async () => {
    if (!proyectoInforme) return;
    try {
      const res = await api.get(`/proyectos/${proyectoInforme.id}/fichajes/export`, {
        params: { desde: picadosDesde, hasta: picadosHasta },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `picados_${proyectoInforme.codigo}_${picadosDesde}_${picadosHasta}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const abrirGastos = async (p) => {
    setProyectoInforme(p);
    setGastosOpen(true);
    setGastosData(null);
    try {
      const { data } = await api.get(`/proyectos/${p.id}/gastos`);
      setGastosData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const darBaja = async (asig) => {
    if (!window.confirm(`¿Dar de baja a ${asig.usuario} en este proyecto?`)) return;
    try {
      await api.post(`/proyectos/asignaciones/${asig.id}/baja`, {});
      toast.success("Baja registrada");
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!cargandoEmpresa && empresa && !empresa.gestionar_proyectos) {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div className="space-y-5">
      <PageHeader
        icon={Briefcase}
        title="Proyectos"
        subtitle="Planificación visual, imputación y rentabilidad de proyectos"
        sectionKey="proyectos"
        brandGradient={false}
      >
        <Button onClick={abrirNuevo} className="gap-2 bg-white/20 text-white hover:bg-white/30">
          <Plus className="h-4 w-4" /> Nuevo proyecto
        </Button>
      </PageHeader>

      <Tabs value={vista} onValueChange={setVista}>
        <TabsList>
          <TabsTrigger value="tarjetas" className="gap-1.5"><Briefcase className="h-3.5 w-3.5" /> Tarjetas</TabsTrigger>
          <TabsTrigger value="calendario" className="gap-1.5"><CalendarRange className="h-3.5 w-3.5" /> Calendario</TabsTrigger>
          <TabsTrigger value="timeline" className="gap-1.5"><LayoutGrid className="h-3.5 w-3.5" /> Timeline</TabsTrigger>
          <TabsTrigger value="clientes" className="gap-1.5"><Users className="h-3.5 w-3.5" /> Clientes</TabsTrigger>
          <TabsTrigger value="rentabilidad" className="gap-1.5"><ChartNoAxesCombined className="h-3.5 w-3.5" /> Análisis</TabsTrigger>
        </TabsList>
      </Tabs>

      {vista === "timeline" && <ProyectosTimeline empleados={empleados} />}
      {vista === "tarjetas" && (
        cargando ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-violet-500" /></div>
        ) : proyectosOrdenados.length === 0 && proyectosInactivos.length === 0 ? (
          <p className="rounded-xl border border-dashed border-zinc-200 py-16 text-center text-sm text-zinc-500">
            Crea tu primer proyecto para empezar a planificar.
          </p>
        ) : (
          <div className="flex flex-col gap-4">
            {proyectosOrdenados.length === 0 ? (
              <p className="rounded-xl border border-dashed border-zinc-200 py-10 text-center text-sm text-zinc-500">
                No hay proyectos activos.
              </p>
            ) : (
              proyectosOrdenados.map((p) => (
                <TarjetaProyecto
                  key={p.id}
                  proyecto={p}
                  onAsignar={abrirAsignar}
                  onAsignarHorario={abrirAsignarHorario}
                  onVerEquipo={abrirEquipo}
                  onEditar={abrirEditar}
                  onEliminar={eliminarProyecto}
                  onPicados={abrirPicados}
                  onGastos={abrirGastos}
                  onReactivar={reactivarProyecto}
                  onTareas={setTareasProyecto}
                  onRentabilidad={setRentabilidadProyecto}
                />
              ))
            )}

            {proyectosInactivos.length > 0 && (
              <div className="mt-2 border-t border-zinc-200 pt-4">
                <button
                  type="button"
                  className="flex items-center gap-2 text-sm font-medium text-zinc-600 hover:text-zinc-900"
                  onClick={() => setVerInactivos((v) => !v)}
                >
                  {verInactivos ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  Proyectos inactivos ({proyectosInactivos.length})
                </button>
                {verInactivos && (
                  <div className="mt-3 flex flex-col gap-3">
                    {proyectosInactivos.map((p) => (
                      <TarjetaProyecto
                        key={p.id}
                        proyecto={p}
                        onAsignar={abrirAsignar}
                        onAsignarHorario={abrirAsignarHorario}
                        onVerEquipo={abrirEquipo}
                        onEditar={abrirEditar}
                        onEliminar={eliminarProyecto}
                        onPicados={abrirPicados}
                        onGastos={abrirGastos}
                        onReactivar={reactivarProyecto}
                        onTareas={setTareasProyecto}
                        onRentabilidad={setRentabilidadProyecto}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )
      )}

      {vista === "calendario" && <ProyectosCalendario />}
      {vista === "clientes" && <ProyectosClientes onChange={cargarClientes} />}
      {vista === "rentabilidad" && <ProyectosRentabilidad clientes={clientes} proyectos={items} empleados={empleados} />}

      {proyOpen && (
        <Dialog open onOpenChange={setProyOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editId ? "Editar proyecto" : "Nuevo proyecto"}</DialogTitle>
            </DialogHeader>
            {!editId ? (
              <FormProyectoCrear form={formProy} setForm={setFormProy} clientes={clientes} />
            ) : (
              <FormProyectoEditar form={formEdit} setForm={setFormEdit} clientes={clientes} />
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setProyOpen(false)}>Cancelar</Button>
              <Button onClick={guardarProyecto}>{editId ? "Guardar" : "Crear"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {horarioOpen && proyectoHorario && (
        <Dialog open onOpenChange={setHorarioOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Horario — «{proyectoHorario.nombre}»</DialogTitle>
              <DialogDescription>
                Define el horario de trabajo del proyecto. Los trabajadores asignados lo usarán salvo que indiques uno distinto al asignarlos.
              </DialogDescription>
            </DialogHeader>
            <HorarioEditor horario={horarioForm} onChange={setHorarioForm} />
            <DialogFooter>
              <Button variant="outline" onClick={() => setHorarioOpen(false)}>Cancelar</Button>
              <Button onClick={guardarHorario}>Guardar horario</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {equipoOpen && proyectoEquipo && (
        <Dialog open onOpenChange={(v) => { setEquipoOpen(v); if (!v) setProyectoEquipoId(null); }}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Equipo — «{proyectoEquipo.nombre}»</DialogTitle>
              <DialogDescription>
                {(proyectoEquipo.asignaciones || []).length} trabajador
                {(proyectoEquipo.asignaciones || []).length === 1 ? "" : "es"} asignado
                {(proyectoEquipo.asignaciones || []).length === 1 ? "" : "s"}
              </DialogDescription>
            </DialogHeader>
            {(proyectoEquipo.asignaciones || []).length === 0 ? (
              <p className="rounded-lg border border-dashed border-zinc-200 py-8 text-center text-sm text-zinc-500">
                Aún no hay trabajadores en este proyecto.
              </p>
            ) : (
              <ul className="space-y-2">
                {(proyectoEquipo.asignaciones || []).map((a) => {
                  const per = ESTADO_PERIODO_ASIG[a.periodo_estado] || ESTADO_PERIODO_ASIG.activa;
                  return (
                  <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-zinc-100 bg-zinc-50 px-3 py-2.5">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium text-zinc-900">{a.usuario}</p>
                        <Badge className={`text-[10px] ${per.cls}`}>{per.label}</Badge>
                      </div>
                      <p className="text-xs text-zinc-500">
                        {fmtFecha(a.fecha_inicio)}
                        {a.fecha_fin ? ` → ${fmtFecha(a.fecha_fin)}` : " → Indefinido"}
                        {" · "}{a.carga_pct}% carga
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {a.estado === "pendiente_confirmacion" && (
                        <Badge className="bg-amber-100 text-[10px] text-amber-800">Pend. horario</Badge>
                      )}
                      <Button type="button" variant="outline" size="sm" className="h-8 text-xs text-rose-600" onClick={() => darBaja(a)}>
                        Baja
                      </Button>
                    </div>
                  </li>
                  );
                })}
              </ul>
            )}
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => { setEquipoOpen(false); setProyectoEquipoId(null); }}>
                Cerrar
              </Button>
              <Button className="gap-1.5" onClick={() => { setEquipoOpen(false); abrirAsignar(proyectoEquipo); }}>
                <Plus className="h-4 w-4" /> Asignar trabajador
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {asigOpen && proyectoSel && (
        <Dialog open onOpenChange={setAsigOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Asignar a «{proyectoSel.nombre}»</DialogTitle>
              <DialogDescription>
                Fechas por defecto del proyecto ({proyectoSel.fecha_inicio ? fmtFecha(proyectoSel.fecha_inicio) : "—"}
                {proyectoSel.fecha_fin ? ` → ${fmtFecha(proyectoSel.fecha_fin)}` : ""}). Puedes modificarlas.
                Un trabajador puede estar asignado a varios proyectos a la vez; si hay solape de fechas se mostrará un aviso informativo.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Trabajador *</Label>
                <Select value={formAsig.usuario_id} onValueChange={(v) => setFormAsig({ ...formAsig, usuario_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    {empleados.map((u) => (
                      <SelectItem key={u.id} value={String(u.id)}>{u.nombre} {u.apellidos}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Inicio *</Label>
                  <Input
                    type="date"
                    min={proyectoSel.fecha_inicio || undefined}
                    max={proyectoSel.fecha_fin || undefined}
                    value={formAsig.fecha_inicio}
                    onChange={(e) => setFormAsig({ ...formAsig, fecha_inicio: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Fin</Label>
                  <Input
                    type="date"
                    min={formAsig.fecha_inicio || proyectoSel.fecha_inicio || undefined}
                    max={proyectoSel.fecha_fin || undefined}
                    value={formAsig.fecha_fin}
                    onChange={(e) => setFormAsig({ ...formAsig, fecha_fin: e.target.value })}
                  />
                </div>
              </div>
              <div><Label>Carga (%)</Label><Input type="number" min={1} max={100} value={formAsig.carga_pct} onChange={(e) => setFormAsig({ ...formAsig, carga_pct: e.target.value })} /></div>
              <div className="flex items-center gap-2 rounded-lg border border-violet-100 bg-violet-50/50 p-3">
                <Switch checked={horarioPropio} onCheckedChange={setHorarioPropio} id="hor-propio" />
                <Label htmlFor="hor-propio" className="cursor-pointer text-sm">
                  Horario distinto al del proyecto
                </Label>
              </div>
              {horarioPropio && (
                <HorarioEditor horario={formAsig.horario} onChange={(h) => setFormAsig({ ...formAsig, horario: h })} />
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAsigOpen(false)}>Cancelar</Button>
              <Button onClick={() => enviarAsignacion()}>Asignar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {picadosOpen && proyectoInforme && (
        <Dialog open onOpenChange={(v) => { setPicadosOpen(v); if (!v) setProyectoInforme(null); }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Picados — {proyectoInforme.codigo} · {proyectoInforme.nombre}</DialogTitle>
              <DialogDescription>Entradas y salidas de trabajadores en este proyecto</DialogDescription>
            </DialogHeader>
            <div className="flex flex-wrap items-end gap-3">
              <div><Label>Desde</Label><Input type="date" value={picadosDesde} onChange={(e) => setPicadosDesde(e.target.value)} /></div>
              <div><Label>Hasta</Label><Input type="date" value={picadosHasta} onChange={(e) => setPicadosHasta(e.target.value)} /></div>
              <div>
                <Label>Agrupar por</Label>
                <Select value={picadosAgrupar} onValueChange={setPicadosAgrupar}>
                  <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dia">Día</SelectItem>
                    <SelectItem value="trabajador">Trabajador</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={cargarPicados} disabled={cargandoInforme}>Consultar</Button>
              <Button variant="outline" className="gap-1.5" onClick={exportarPicados} disabled={!picadosData?.filas?.length}>
                <Download className="h-4 w-4" /> Excel
              </Button>
            </div>
            {cargandoInforme ? (
              <div className="flex justify-center py-10"><Loader2 className="h-8 w-8 animate-spin text-violet-500" /></div>
            ) : picadosData ? (
              <div className="space-y-4">
                <p className="text-sm text-zinc-600">
                  {picadosData.total_sesiones} sesiones · Total: <strong>{picadosData.total_horas}</strong>
                </p>
                {picadosData.grupos?.length ? picadosData.grupos.map((g) => (
                  <div key={g.titulo} className="rounded-lg border border-zinc-200">
                    <p className="border-b bg-zinc-50 px-3 py-2 text-sm font-semibold">{g.titulo}</p>
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-xs text-zinc-500"><th className="p-2">Día</th><th className="p-2">Fecha</th><th className="p-2">Trabajador</th><th className="p-2">Entrada</th><th className="p-2">Salida</th><th className="p-2">Duración</th></tr></thead>
                      <tbody>
                        {g.filas.map((r, i) => (
                          <tr key={i} className="border-t border-zinc-100">
                            <td className="p-2">{r.dia_semana}</td>
                            <td className="p-2">{r.fecha_fmt}</td>
                            <td className="p-2">{r.usuario}</td>
                            <td className="p-2">{r.entrada_hora}</td>
                            <td className="p-2">{r.salida_hora}</td>
                            <td className="p-2">{r.duracion}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )) : (
                  <p className="py-8 text-center text-sm text-zinc-500">Sin picados en el periodo seleccionado.</p>
                )}
              </div>
            ) : (
              <p className="py-8 text-center text-sm text-zinc-500">Pulsa Consultar para cargar los datos.</p>
            )}
          </DialogContent>
        </Dialog>
      )}

      {gastosOpen && proyectoInforme && (
        <Dialog open onOpenChange={(v) => { setGastosOpen(v); if (!v) setProyectoInforme(null); }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Gastos — {proyectoInforme.codigo} · {proyectoInforme.nombre}</DialogTitle>
            </DialogHeader>
            {gastosData ? (
              <>
                <p className="text-sm text-zinc-600">Total aprobado: <strong>{gastosData.total_aprobado?.toFixed(2)} €</strong></p>
                {gastosData.items?.length ? (
                  <ul className="divide-y rounded-lg border">
                    {gastosData.items.map((g) => (
                      <li key={g.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                        <div>
                          <p className="font-medium">{g.usuario}</p>
                          <p className="text-xs text-zinc-500">{g.proveedor || "—"} · {g.fecha_gasto ? fmtFecha(g.fecha_gasto) : "—"}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium">{g.importe != null ? `${g.importe} €` : "—"}</p>
                          <Badge variant="outline" className="text-[10px]">{g.estado}</Badge>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="py-8 text-center text-sm text-zinc-500">No hay gastos imputados a este proyecto.</p>
                )}
              </>
            ) : (
              <div className="flex justify-center py-10"><Loader2 className="h-8 w-8 animate-spin text-violet-500" /></div>
            )}
          </DialogContent>
        </Dialog>
      )}

      <ProyectoTareasDialog proyecto={tareasProyecto} open={!!tareasProyecto} onOpenChange={(v) => !v && setTareasProyecto(null)} onChange={cargar} />
      <Dialog open={!!rentabilidadProyecto} onOpenChange={(v) => !v && setRentabilidadProyecto(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Rentabilidad — {rentabilidadProyecto?.nombre}</DialogTitle></DialogHeader>
          {rentabilidadProyecto && <ProyectosRentabilidad clientes={clientes} proyectos={items} empleados={empleados} proyectoId={rentabilidadProyecto.id} />}
        </DialogContent>
      </Dialog>

    </div>
  );
}
